import { ScoredStock, StockFactors } from '@/lib/compute';
import { QUALITY_FACTORS, factorLabel } from '@/lib/clientScore';
import type { FactorConfig, PillarMapping } from '@/lib/factor-config';
import { DEFAULT_PILLAR_WEIGHTS } from '@/lib/factor-config';
import { fmtVal } from '@/lib/stock-detail-helpers';
import { unionPillarMapping } from '@/lib/pillar-factors';
import { FactorBar } from './FactorBar';
import { FactorDetailPanel, type FactorDetailData } from './FactorDetailPanel';
import { InsiderDetail } from './InsiderDetail';

/** Per-stock absolute valuation lens data — the 3 universe-z signals that
 *  live OUTSIDE PILLAR_FACTORS but score into V_blended at 25% via vAbsoluteC.
 *  Source: /api/target-prices. Steve directive 2026-05-11: surface them in
 *  the Factor Breakdown popup. */
export interface AbsoluteLensRow {
  asymZ: number | null;
  dcfDiscZ: number | null;
  todayDiscZ: number | null;
  asymmetry: number | null;
  dcfFairValue: number | null;
  todayFairValue: number | null;
  fiveYearTarget: number | null;
  currentPrice: number | null;
  vAbsoluteC: number | null;
}

interface Props {
  stock: ScoredStock;
  pillarMapping: PillarMapping;
  fwdPctByPillar: Record<string, number | null>;
  peerGroupLabel: string | null;
  getZScore: (key: keyof StockFactors) => number | null;
  getPercentile: (key: keyof StockFactors, invert: boolean) => number | null;
  scoreColor: (v: number | null) => string;
  expandedFactor: string | null;
  setExpandedFactor: (updater: (prev: string | null) => string | null) => void;
  factorDetail: FactorDetailData | null;
  factorDetailLoading: boolean;
  insiderExpanded: boolean;
  setInsiderExpanded: (updater: (prev: boolean) => boolean) => void;
  /** Per-stock absolute valuation row from /api/target-prices, when available. */
  absoluteLens?: AbsoluteLensRow | null;
}

/** Format a ratio as a percentage with 1 decimal. Guards against very large/small outliers. */
function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  // Clamp display to avoid rendering e.g. 2000% for unusual stocks; still shows the value.
  return `${(v * 100).toFixed(1)}%`;
}

// Derived from the canonical weights (lib/factor-config.ts) so the label can never
// drift from the real scoring weights again. Was hardcoded 15/45/10/10 (stale April vector).
const PILLAR_PCT: Record<'profitability' | 'safety' | 'growth' | 'shareholderReturns', string> = {
  profitability: `${DEFAULT_PILLAR_WEIGHTS.profitability}%`,
  safety: `${DEFAULT_PILLAR_WEIGHTS.safety}%`,
  growth: `${DEFAULT_PILLAR_WEIGHTS.growth}%`,
  shareholderReturns: `${DEFAULT_PILLAR_WEIGHTS.shareholderReturns}%`,
};
const LEFT_COL_PILLARS = ['profitability', 'safety'] as const;
const RIGHT_COL_PILLARS = ['growth', 'shareholderReturns'] as const;

export function FactorBreakdown({
  stock,
  pillarMapping,
  fwdPctByPillar,
  peerGroupLabel,
  getZScore,
  getPercentile,
  scoreColor,
  expandedFactor,
  setExpandedFactor,
  factorDetail,
  factorDetailLoading,
  insiderExpanded,
  setInsiderExpanded,
  absoluteLens,
}: Props) {
  // Union of factor keys across BOTH flavors, computed once. Lets us render
  // factors NOT in this stock's flavor as greyed-out, so the user sees the
  // full lens set and which ones apply to this peer group.
  const unionMap = unionPillarMapping();

  /** Build a fast lookup of "is this factor scoring on this stock". */
  const activeKeysByPillar = new Map<string, Set<string>>();
  for (const pillar of ['profitability', 'growth', 'safety', 'shareholderReturns', 'valuation', 'momentum'] as const) {
    const pm = pillarMapping[pillar as keyof PillarMapping];
    activeKeysByPillar.set(pillar, new Set((pm ?? []).map(f => String(f.key))));
  }

  const renderFactorRow = (key: string, invert: boolean, weight: number | undefined, opts?: { isInsider?: boolean; inactive?: boolean }) => {
    if (opts?.inactive) {
      // Greyed-out: factor is not scoring this stock (different peer group).
      // Render at low opacity, no figure, no z-score — just label + "n/a" so
      // the user can see the factor exists but isn't applied here.
      return (
        <div key={key} className="opacity-30 pointer-events-none" title={`Not scored for ${peerGroupLabel ?? 'this peer group'}`}>
          <FactorBar
            label={factorLabel(key)}
            rawValue="—"
            zscore={null}
            percentile={null}
            invert={invert}
            weight={weight}
          />
        </div>
      );
    }
    return (
      <div key={key}>
        <div
          className="cursor-pointer hover:bg-[#1e293b]/50 rounded -mx-1 px-1"
          onClick={() => {
            if (opts?.isInsider) {
              setInsiderExpanded(prev => !prev);
              setExpandedFactor(() => null);
            } else {
              setExpandedFactor(prev => prev === key ? null : key);
              setInsiderExpanded(() => false);
            }
          }}
        >
          <FactorBar
            label={factorLabel(key)}
            rawValue={fmtVal(stock.factors[key as keyof StockFactors], key)}
            zscore={getZScore(key as keyof StockFactors)}
            percentile={getPercentile(key as keyof StockFactors, invert)}
            invert={invert}
            weight={weight}
          />
        </div>
        {opts?.isInsider && insiderExpanded && <InsiderDetail symbol={stock.symbol} />}
        {!opts?.isInsider && expandedFactor === key && (
          <FactorDetailPanel factorKey={key} data={factorDetail} loading={factorDetailLoading} />
        )}
      </div>
    );
  };

  const renderPillarHeader = (
    title: string,
    pillarPct: string | null,
    fwdPct: number | null,
    pillarScore: number | null | undefined,
    accentColor?: string,
  ) => {
    const bwPct = fwdPct != null ? 100 - fwdPct : null;
    return (
      <div
        className={`flex items-center justify-between text-[9px] font-semibold uppercase tracking-widest mt-2 mb-1 pt-1.5 border-t ${accentColor ? '' : 'border-[#1e293b] text-white'}`}
        style={accentColor ? { borderColor: `${accentColor}30`, color: accentColor } : undefined}
      >
        <span>
          {title}
          {pillarPct && <span className="text-[7px] text-[#64748b] normal-case font-normal"> {pillarPct}</span>}
          {fwdPct != null && (
            <span className="text-[7px] text-[#64748b] normal-case font-normal ml-1">
              · <span className="text-[#4ade80]">{fwdPct}% fwd</span> · <span className="text-[#94a3b8]">{bwPct}% bw</span>
            </span>
          )}
        </span>
        <span className="text-[10px] tabular-nums" style={{ color: scoreColor(pillarScore ?? null), fontFamily: 'var(--font-mono)' }}>
          {pillarScore != null ? (pillarScore >= 0 ? '+' : '') + pillarScore.toFixed(2) : '--'}
        </span>
      </div>
    );
  };

  const renderQualityPillar = (pillar: 'profitability' | 'safety' | 'growth' | 'shareholderReturns') => {
    const title = pillar === 'shareholderReturns' ? 'Shareholder Returns' : pillar;
    // Render UNION of factors per pillar (across all flavors). Active ones
    // (in this stock's pillarMapping) show normal; inactive ones grey out.
    // Falls back to QUALITY_FACTORS when union isn't typed (safety net).
    const unionFactors = (unionMap[pillar] ?? pillarMapping[pillar] ?? QUALITY_FACTORS[pillar]) as FactorConfig[];
    const activeKeys = activeKeysByPillar.get(pillar) ?? new Set<string>();

    // Economic spread annotation — displayed inline after ROIC in profitability pillar.
    // Display-only: NOT a scored factor. Shows ROIC − WACC so the user can see whether
    // the company earns above its cost of capital. wacc = dcfDiscountRate from target-prices.json.
    const renderEconomicSpread = () => {
      const roic: number | null = typeof stock.factors?.roic === 'number' ? stock.factors.roic : null;
      const wacc: number | null = typeof (stock as { wacc?: number | null }).wacc === 'number'
        ? (stock as { wacc?: number | null }).wacc!
        : null;
      const spread: number | null = typeof (stock as { economicSpread?: number | null }).economicSpread === 'number'
        ? (stock as { economicSpread?: number | null }).economicSpread!
        : null;
      const hasData = roic != null && wacc != null;
      const spreadColor = spread == null ? '#64748b' : spread > 0 ? '#4ade80' : '#ef4444';
      return (
        <div
          key="economic-spread-annotation"
          className="flex items-baseline justify-between px-1 py-0.5 mb-0.5 bg-[#0f172a]/50 rounded text-[9px]"
          title="Economic profit = ROIC minus WACC (cost of capital). Display-only — not a scored factor."
        >
          <span className="text-[#64748b] uppercase tracking-widest text-[8px]">vs cost of capital</span>
          {hasData ? (
            <span className="tabular-nums" style={{ color: spreadColor, fontFamily: 'var(--font-mono)' }}>
              {fmtPct(roic)} − {fmtPct(wacc)} ={' '}
              <span style={{ color: spreadColor, fontWeight: 600 }}>{fmtPct(spread)}</span>
            </span>
          ) : (
            <span className="text-[#475569] tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>—</span>
          )}
        </div>
      );
    };

    return (
      <div key={pillar} className="mb-2">
        {renderPillarHeader(title, PILLAR_PCT[pillar], fwdPctByPillar[pillar], stock.pillarScores[pillar])}
        {unionFactors.map(({ key, invert, weight }) => {
          const isActive = activeKeys.has(String(key));
          const row = renderFactorRow(String(key), invert, weight, {
            isInsider: key === 'insiderNetBuy',
            inactive: !isActive,
          });
          // Inject economic spread annotation right after the ROIC row in profitability
          if (pillar === 'profitability' && String(key) === 'roic') {
            return [row, renderEconomicSpread()];
          }
          return row;
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0]">
          Factor Breakdown
          {peerGroupLabel && <span className="text-[8px] font-normal text-[#60a5fa] ml-2">({peerGroupLabel})</span>}
        </span>
        <div className="flex items-center gap-2 text-[8px] text-white">
          <span>Weak</span>
          <div className="flex gap-0.5">
            {['#ef4444', '#f97316', '#64748b', '#4ade80', '#22c55e'].map((c, i) => (
              <div key={i} className="w-2 h-1.5 rounded-sm" style={{ backgroundColor: c, opacity: 0.7 }} />
            ))}
          </div>
          <span>Strong</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6">
        <div>
          {LEFT_COL_PILLARS.map(renderQualityPillar)}

          <div className="mb-2">
            {renderPillarHeader('Valuation', null, fwdPctByPillar.valuation, stock.valuationScore)}
            {(() => {
              const activeKeys = activeKeysByPillar.get('valuation') ?? new Set<string>();
              const valFactors = (unionMap.valuation ?? pillarMapping.valuation) as FactorConfig[];
              return valFactors.map(({ key, invert, weight }) => {
                const isActive = activeKeys.has(String(key));
                return renderFactorRow(String(key), invert, weight, { inactive: !isActive });
              });
            })()}

            {/* Absolute lenses — universe-z signals that live outside
                PILLAR_FACTORS but score into V_blended at 25% via vAbsoluteC.
                Shown here so the popup matches the published "12 lenses" count
                (9 peer-z multiples + 3 absolute). Steve directive 2026-05-11. */}
            {absoluteLens && (absoluteLens.asymZ != null || absoluteLens.dcfDiscZ != null || absoluteLens.todayDiscZ != null) && (
              <div className="mt-1 pt-1 border-t border-[#1e293b]/60">
                <div className="text-[7px] uppercase tracking-widest text-[#64748b] mb-0.5 flex items-center justify-between">
                  <span>Absolute Lenses · 25% of V</span>
                  {absoluteLens.vAbsoluteC != null && (
                    <span className="tabular-nums" style={{ color: scoreColor(absoluteLens.vAbsoluteC), fontFamily: 'var(--font-mono)' }}>
                      {absoluteLens.vAbsoluteC >= 0 ? '+' : ''}{absoluteLens.vAbsoluteC.toFixed(2)}
                    </span>
                  )}
                </div>
                <FactorBar
                  label="Asymmetry"
                  rawValue={absoluteLens.asymmetry != null ? `${absoluteLens.asymmetry.toFixed(1)} : 1` : '—'}
                  zscore={absoluteLens.asymZ}
                  percentile={null}
                  invert={false}
                  weight={1}
                />
                <FactorBar
                  label="DCF Discount"
                  rawValue={(absoluteLens.dcfFairValue != null && absoluteLens.currentPrice != null && absoluteLens.currentPrice > 0)
                    ? `${((absoluteLens.dcfFairValue / absoluteLens.currentPrice - 1) * 100).toFixed(1)}%`
                    : '—'}
                  zscore={absoluteLens.dcfDiscZ}
                  percentile={null}
                  invert={false}
                  weight={1}
                />
                <FactorBar
                  label="Today FV Discount"
                  rawValue={(absoluteLens.todayFairValue != null && absoluteLens.currentPrice != null && absoluteLens.currentPrice > 0)
                    ? `${((absoluteLens.todayFairValue / absoluteLens.currentPrice - 1) * 100).toFixed(1)}%`
                    : '—'}
                  zscore={absoluteLens.todayDiscZ}
                  percentile={null}
                  invert={false}
                  weight={1}
                />

                {/* 5-year target — informational reference for the lens block.
                    Not z-scored (the asymZ / dcfDiscZ / todayDiscZ above already
                    encode the same forward-EPS × target-multiple math), but
                    surfacing the dollar value + % upside makes the operational
                    target visible right next to the lenses. Added 2026-05-11. */}
                {absoluteLens.fiveYearTarget != null && absoluteLens.currentPrice != null && absoluteLens.currentPrice > 0 && (() => {
                  const target = absoluteLens.fiveYearTarget;
                  const upsidePct = (target / absoluteLens.currentPrice - 1) * 100;
                  const annPct = (Math.pow(target / absoluteLens.currentPrice, 1 / 5) - 1) * 100;
                  const tone = upsidePct >= 50 ? '#10b981' : upsidePct >= 20 ? '#f59e0b' : '#94a3b8';
                  return (
                    <div className="flex items-baseline justify-between mt-1 pt-1 border-t border-[#1e293b]/40 text-[10px]">
                      <span className="uppercase tracking-widest text-[#94a3b8] text-[7px]">5-Yr Target</span>
                      <span className="tabular-nums" style={{ color: tone, fontFamily: 'var(--font-mono)' }}>
                        ${target.toFixed(0)}
                        <span className="text-[#64748b] ml-1">
                          ({upsidePct >= 0 ? '+' : ''}{upsidePct.toFixed(0)}% / {annPct >= 0 ? '+' : ''}{annPct.toFixed(1)}% ann)
                        </span>
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        <div>
          {RIGHT_COL_PILLARS.map(renderQualityPillar)}

          {pillarMapping.momentum && (
            <div className="mb-2">
              {renderPillarHeader('Momentum', null, null, stock.pillarScores.momentum, '#f59e0b')}
              {(() => {
                const activeKeys = activeKeysByPillar.get('momentum') ?? new Set<string>();
                const momFactors = (unionMap.momentum ?? pillarMapping.momentum) as FactorConfig[];
                return momFactors.map(({ key, invert, weight }) => {
                  const isActive = activeKeys.has(String(key));
                  return renderFactorRow(String(key), invert, weight, { inactive: !isActive });
                });
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
